// The executable guidance ROOTS — every guidance module a task can be told to run.
//
// A root's identity is its PATH under src/guidance, e.g. 'polish/security/script-review.ts'. There used
// to be a second, parallel namespace: src/guidance/manifest.json gave every entry an arbitrary key
// ('polish-script-security-review'), the deploy bundled one archive per key, and a task carried the key.
// That namespace was the shape of the retired guidance_entries DB table, where the key WAS the primary
// key. With guidance as repo source the path already identifies the module, and the second namespace
// bought nothing but a chance to disagree with itself:
//
//   • Producers built keys by interpolation (`polish-script-${passType}`) because keys mostly followed a
//     convention. The security pass is the one whose roots do not — it has TWO (review + probe) — so
//     every security Pass-2 task asked for 'polish-script-security', got nothing back, and died at
//     startup. Nothing caught it: no check compared a REQUESTED key against the manifest.
//   • 248 archives were built and shipped per deploy; about a dozen keys were ever requested.
//
// So: producers name a root through the typed constants below (a typo is a type error), the value that
// travels to a container is the path, and the container builds that path out of its own checkout.
//
// This module is dependency-free apart from the pass config it derives polish roots from — it is
// imported by backend functions, container harnesses, and guidance scripts alike.
import passesConfig from './polish/runner/passes.json'

// Roots named directly by the backend. The property names are code-level identifiers only: nothing
// persists them, so renaming one is a local refactor.
export const ROOTS = {
  playwrightFailure: 'playwright-failure/script.ts',
  explorationRun: 'exploration/run-script.ts',
  journeyRun: 'journey/run-script.ts',
  journeyRepair: 'journey-repair/script.ts',
  sessionAnalysis: 'user-session/analyze-script.ts',
  versionAddedRun: 'chat/version-added-run-script.ts',
  fileJourneyBug: 'journey/file-journey-bug-script.ts',
  cloneBuild: 'clone/build-script.ts',
  fixMismatchReview: 'fix-mismatch-review/script.ts',
  sessionReviewer: 'session-reviewers/script.ts',
  sessionSummarizer: 'session-summarizers/script.ts',
  regenerateSessionSubtext: 'user-session/regenerate-script.ts',
  userSessionAuthor: 'user-session/author-script.ts',
  evalInstantiate: 'evals/instantiate-script.ts',
  evalProcess: 'evals/process-script.ts',
  evalVerdict: 'evals/verdict-script.ts',
  criticRun: 'admin/critic-run-script.ts',
  adversary: 'admin/adversary/script.ts',
  errorProcessing: 'feedback/error-processing-script.ts',
  // The security pass's roots (also listed in polish/runner/passes.json); the probe-security eval spawns
  // the probe directly and security results read both.
  securityReview: 'polish/security/script-review.ts',
  securityProbe: 'polish/security/script-probe.ts',
  // Not a task root: the in-page recorder script, built as a browser IIFE rather than run under Node.
  // Listed here so the one place that names it is the same place as every other root.
  recordingPolyfills: 'recording/injected-polyfills.ts',
  recordingInjected: 'recording/injected-script.ts',
} as const

// The polish passes' roots, read from the pass config so the config stays the single place that says
// which script a pass runs (a pass may declare several — security runs a passive review and an active
// probe). Paths here are validated against the filesystem by scripts/check-guidance-roots.ts.
interface PassConfigShape {
  passes?: Array<{ id?: string; tasks?: Array<{ scriptGuidance?: string; readsRecordingEvents?: boolean; signals?: string[]; files?: string[] }> }>
}

// Whether a root reads its recording's prefetched rrweb / injected events (POLISH_RRWEB_FILE /
// POLISH_INJECTED_FILE), as its pass task declares in passes.json. Only those roots get the prefetch.
export function readsRecordingEvents(root: string): boolean {
  if (root === ROOTS.sessionAnalysis) return true
  return ((passesConfig as PassConfigShape).passes ?? [])
    .some((p) => (p.tasks ?? []).some((t) => t.scriptGuidance === root && t.readsRecordingEvents === true))
}

function polishTasksFor(passType: string) {
  return ((passesConfig as PassConfigShape).passes ?? []).find((p) => p.id === passType)?.tasks ?? []
}

export function polishRootsFor(passType: string): string[] {
  return polishTasksFor(passType).map((t) => t.scriptGuidance).filter((s): s is string => typeof s === 'string' && s.length > 0)
}

// The Pass-2 candidates one of a pass's roots confirms. A task that declares `signals` confirms only those;
// a task without them confirms every candidate no sibling task claims.
export function polishRootCandidates<T extends { signal: string }>(passType: string, root: string, candidates: readonly T[]): T[] {
  const tasks = polishTasksFor(passType)
  const own = tasks.find((t) => t.scriptGuidance === root)?.signals
  if (own) return candidates.filter((c) => own.includes(c.signal))
  const claimed = new Set(tasks.flatMap((t) => t.signals ?? []))
  return candidates.filter((c) => !claimed.has(c.signal))
}

// Whether a pass's root gets a Pass-2 followup for these candidates. A root declaring `signals` has nothing
// to do without one of them; a root without `signals` runs regardless (e.g. security's probe root).
export function polishRootSpawns(passType: string, root: string, candidates: readonly { signal: string }[]): boolean {
  const own = polishTasksFor(passType).find((t) => t.scriptGuidance === root)?.signals
  return !own || polishRootCandidates(passType, root, candidates).length > 0
}

// The pass whose task files `signal` in passes.json, or null when no task claims it. A task's `files`
// defaults to its `signals`; it is narrower when the task confirms a whole signal family but files only some
// of its signals (polish/glitches/registry.ts familyOwners).
export function polishSignalOwner(signal: string): string | null {
  return ((passesConfig as PassConfigShape).passes ?? [])
    .find((p) => (p.tasks ?? []).some((t) => (t.files ?? t.signals)?.includes(signal)))?.id ?? null
}

// The followup LABEL for one of a pass's Pass-2 roots. A recording followup is deduped per (owner, label)
// — spawning twice with the same label returns the FIRST task rather than a second one — so a pass with
// several roots must give each a distinct label or every root after the first is silently discarded. A
// single-root pass keeps its bare pass name (the label a task's page shows, and the value in flight).
export function polishFollowupLabel(passType: string, root: string): string {
  if (polishRootsFor(passType).length < 2) return passType
  const leaf = root.split('/').pop()?.replace(/\.[cm]?[jt]s$/, '') ?? root
  return `${passType}:${leaf}`
}

const POLISH_ROOT_PATHS: string[] = ((passesConfig as PassConfigShape).passes ?? []).flatMap((p) =>
  (p.tasks ?? []).map((t) => t.scriptGuidance).filter((s): s is string => typeof s === 'string' && s.length > 0),
)

/** Every path a task may legitimately be asked to run. */
export const GUIDANCE_ROOT_PATHS: readonly string[] = [...new Set([...Object.values(ROOTS), ...POLISH_ROOT_PATHS])]

const ROOT_SET = new Set(GUIDANCE_ROOT_PATHS)

// Manifest key → root path. Stored task rows (tasks.followup_script) hold either form, and readers of
// historical rows (metrics, journey/security result queries) must match both, so this map stays.
const MANIFEST_KEY_ROOTS: Readonly<Record<string, string>> = {
  'clone-build-script': ROOTS.cloneBuild,
  'critic-run-script': ROOTS.criticRun,
  'error-processing-script': ROOTS.errorProcessing,
  'eval-instantiate-script': ROOTS.evalInstantiate,
  'eval-process-script': ROOTS.evalProcess,
  'eval-verdict-script': ROOTS.evalVerdict,
  'exploration-run-script': ROOTS.explorationRun,
  'file-journey-bug-script': ROOTS.fileJourneyBug,
  'journey-run-script': ROOTS.journeyRun,
  'recording-injected-script': ROOTS.recordingInjected,
  'version-added-run-script': ROOTS.versionAddedRun,
  'polish-script-accessibility': 'polish/accessibility/script.ts',
  'polish-script-glitches': 'polish/glitches/script.ts',
  'polish-script-layout-shift': 'polish/layout-shift/script.ts',
  'polish-script-network-performance': 'polish/network-performance/script.ts',
  'polish-script-react-rendering': 'polish/react-rendering/script.ts',
  'polish-script-seo': 'polish/seo/script.ts',
  'polish-script-security-probe': ROOTS.securityProbe,
  'polish-script-security-review': ROOTS.securityReview,
  'polish-script-ui-details': 'polish/ui-details/script.ts',
  'polish-script-user-experience': 'polish/user-experience/script.ts',
}

/**
 * The root path for a stored/received value: a path passes through, a manifest key maps to its path,
 * anything else is null so the caller can fail loudly instead of asking the container to run a name that
 * does not resolve (otherwise a missing root surfaces 4 seconds into a container, as a failed task with no
 * filed bug).
 */
export function resolveGuidanceRoot(value: string | null | undefined): string | null {
  if (!value) return null
  if (ROOT_SET.has(value)) return value
  return MANIFEST_KEY_ROOTS[value] ?? null
}

/**
 * Every stored form of a root: its path plus each manifest key that maps to it. For SQL, which cannot call
 * resolveGuidanceRoot: `followup_script = ANY(${guidanceRootReferences(ROOTS.x)}::text[])`.
 */
export function guidanceRootReferences(root: string): string[] {
  return [root, ...Object.keys(MANIFEST_KEY_ROOTS).filter((key) => MANIFEST_KEY_ROOTS[key] === root)]
}
